/**
 * CopyCreativeModal component for copying creatives to other projects
 * Shows dropdown of projects in current team (excluding source project)
 * Displays preview of new name with " (Copy)" suffix
 */

import { Copy, X, Loader2, Info, ChevronDown } from 'lucide-react';
import * as React from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@/components/ui/Button';
import { useAuth } from '@/context/AuthContext';
import { useProjects } from '@/hooks/useProjects';
import { cn } from '@/lib/utils';
import type { Creative, Project } from '@/types';

// ============================================================================
// TYPES
// ============================================================================

export interface CopyCreativeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCopy: (targetProjectId: string) => Promise<void>;
  creative: Creative;
  isLoading?: boolean;
}

// ============================================================================
// MODAL OVERLAY COMPONENT
// ============================================================================

function ModalOverlay({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClick();
        }
      }}
    >
      {children}
    </div>
  );
}

// ============================================================================
// PROJECT DROPDOWN COMPONENT
// ============================================================================

interface ProjectDropdownProps {
  projects: Project[];
  selectedProjectId: string | null;
  onSelect: (projectId: string) => void;
  disabled?: boolean;
}

function ProjectDropdown({
  projects,
  selectedProjectId,
  onSelect,
  disabled = false,
}: ProjectDropdownProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const selectedProject = projects.find((p) => p.projectId === selectedProjectId);

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          'w-full flex items-center justify-between gap-2 px-4 py-3 rounded-lg',
          'bg-surface border border-border-subtle text-left',
          'hover:border-border-strong transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/50',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          isOpen && 'border-brand ring-2 ring-brand/20'
        )}
      >
        <span className={cn('text-sm', selectedProject ? 'text-primary' : 'text-muted')}>
          {selectedProject ? selectedProject.name : 'Select a project...'}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-muted transition-transform',
            isOpen && 'transform rotate-180'
          )}
        />
      </button>

      {isOpen && projects.length > 0 && (
        <div className="absolute z-10 w-full mt-2 bg-surface rounded-lg shadow-lg border border-border-subtle py-1 max-h-60 overflow-auto">
          {projects.map((project) => (
            <button
              key={project.projectId}
              type="button"
              onClick={() => {
                onSelect(project.projectId);
                setIsOpen(false);
              }}
              className={cn(
                'w-full flex flex-col items-start px-4 py-2.5 text-left',
                'hover:bg-surface-subtle transition-colors',
                selectedProjectId === project.projectId && 'bg-brand-soft'
              )}
            >
              <span className="text-sm font-medium text-primary">{project.name}</span>
              {project.brandName && (
                <span className="text-xs text-muted">{project.brandName}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {isOpen && projects.length === 0 && (
        <div className="absolute z-10 w-full mt-2 bg-surface rounded-lg shadow-lg border border-border-subtle p-4">
          <p className="text-sm text-muted text-center">
            No other projects available in this team
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function CopyCreativeModal({
  isOpen,
  onClose,
  onCopy,
  creative,
  isLoading = false,
}: CopyCreativeModalProps) {
  const { currentTeam } = useAuth();
  const { data: allProjects, isLoading: projectsLoading } = useProjects(currentTeam?.teamId);
  const [selectedProjectId, setSelectedProjectId] = React.useState<string | null>(null);
  const [isCopying, setIsCopying] = React.useState(false);
  const modalRef = React.useRef<HTMLDivElement>(null);

  // Filter projects: exclude the source project
  const availableProjects = React.useMemo(() => {
    if (!allProjects) return [];
    return allProjects.filter(
      (p) => p.projectId !== creative.projectId && p.status === 'active'
    );
  }, [allProjects, creative.projectId]);

  // Reset state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setSelectedProjectId(null);
      setIsCopying(false);
    }
  }, [isOpen]);

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isLoading && !isCopying) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isLoading, isCopying, onClose]);

  // Focus trap
  React.useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen]);

  // Calculate new name with " (Copy)" suffix
  const newName = React.useMemo(() => {
    const baseName = creative.name;
    const suffix = ' (Copy)';
    if (baseName.length + suffix.length > 200) {
      return baseName.slice(0, 197 - suffix.length) + '...' + suffix;
    }
    return baseName + suffix;
  }, [creative.name]);

  const handleCopy = async () => {
    if (!selectedProjectId) return;

    setIsCopying(true);
    try {
      await onCopy(selectedProjectId);
      onClose();
    } catch {
      // Error handled by parent
    } finally {
      setIsCopying(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <ModalOverlay onClick={onClose}>
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="copy-modal-title"
        aria-describedby="copy-modal-description"
        tabIndex={-1}
        className={cn(
          'w-full max-w-md bg-surface rounded-xl shadow-xl border border-border-subtle',
          'animate-in zoom-in-95 slide-in-from-bottom-2 duration-200',
          'focus:outline-none'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start gap-4 p-6">
          {/* Icon */}
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-brand-soft flex items-center justify-center">
            <Copy className="h-6 w-6 text-brand" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h2
              id="copy-modal-title"
              className="text-lg font-semibold text-primary leading-tight"
            >
              Copy Creative
            </h2>
            <p
              id="copy-modal-description"
              className="mt-2 text-sm text-secondary leading-relaxed"
            >
              Copy this creative to another project in your team.
            </p>
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            disabled={isLoading || isCopying}
            className={cn(
              'flex-shrink-0 p-1 rounded-md text-muted hover:text-primary hover:bg-surface-subtle transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/50',
              'disabled:opacity-50 disabled:pointer-events-none'
            )}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pb-4 space-y-4">
          {/* Target project selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-primary">
              Destination Project
            </label>
            {projectsLoading ? (
              <div className="flex items-center gap-2 px-4 py-3 bg-surface-subtle rounded-lg">
                <Loader2 className="h-4 w-4 animate-spin text-muted" />
                <span className="text-sm text-muted">Loading projects...</span>
              </div>
            ) : (
              <ProjectDropdown
                projects={availableProjects}
                selectedProjectId={selectedProjectId}
                onSelect={setSelectedProjectId}
                disabled={isLoading || isCopying}
              />
            )}
          </div>

          {/* New name preview */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-primary">
              New Creative Name
            </label>
            <div className="px-4 py-3 bg-surface-subtle rounded-lg border border-border-subtle">
              <span className="text-sm text-primary">{newName}</span>
            </div>
          </div>

          {/* Info message */}
          <div className="flex items-start gap-3 p-3 bg-info-soft rounded-lg">
            <Info className="h-4 w-4 text-info flex-shrink-0 mt-0.5" />
            <p className="text-xs text-info leading-relaxed">
              Analyses will not be copied. You&apos;ll need to re-analyze the creative
              after copying to generate insights with the new project&apos;s brand context.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 pb-6">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isLoading || isCopying}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCopy}
            disabled={
              isLoading ||
              isCopying ||
              !selectedProjectId ||
              projectsLoading ||
              availableProjects.length === 0
            }
            leftIcon={
              isCopying ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Copy className="h-4 w-4" />
              )
            }
          >
            {isCopying ? 'Copying...' : 'Copy Creative'}
          </Button>
        </div>
      </div>
    </ModalOverlay>
  );

  return createPortal(modalContent, document.body);
}
